'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import type {
  QuizAttemptPublicDto,
  QuizAttemptResultDto,
  QuizAnswerInput,
} from '@peach/types/quizzes/quiz-attempt.dto.js';
import { tw } from '@tw';
import {
  startQuizAttempt,
  updateQuizAttempt,
  completeQuizAttempt,
  claimQuizAttempt,
} from '@/app/actions/quizzes';
import {
  recordQuizAttemptStarted,
  recordQuizAttemptCompleted,
  clearPendingQuizClaim,
} from '@/lib/quiz-claim';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { useHeldWork } from '@/components/system/reload-safety';
import { playAnswerBlip, playQuizComplete } from '@/lib/utils/quizSounds';
import { QuizProgressRow } from '@/components/quiz/QuizProgressRow';
import { QuizFinishedCard } from '@/components/quiz/QuizFinishedCard';
import { QuizResult } from '@/components/quiz/QuizResult';
import { QuizOption } from '@/components/quiz/QuizOption';
import { QuizSlam, useQuizSlam, SLAM_MS, slamDuration } from '@/components/quiz/QuizSlam';
import { QUIZ_CARD_MIN_H } from '@/components/quiz/quiz-card';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';

interface QuizCardProps {
  quiz: SanitizedQuiz;
  /** The owner previewing their own page. Analytics only — an owner playing
      their own quiz is testing it, and must not count as a visitor engaging. */
  isOwner: boolean;
  tutorUsername: string;
  /** The name the result's headline says. */
  firstName: string;
  loggedIn: boolean;
  /** On the mate page: the referral edge the gate's sign-in carries as `sourceSquad`. */
  squadAlias?: string | null;
  /** The DM reply the funnel link carried (`?r=`): the DM's follow-up reads the play by it. */
  dmReplyId?: string | null;
}

type Phase = 'inline' | 'playing' | 'finishing' | 'gate' | 'result';

const FLASH_MS = 130;

/**
 * Quiz text, sealed. Not translated (on a language quiz an auto-translation
 * answers the question rather than distorting it), not selectable, and no iOS
 * callout offering Copy and Translate. One place decides what that means.
 */
function Sealed({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span translate="no" className={tw('sf-sealed notranslate', className)}>
      {children}
    </span>
  );
}

export function QuizCard({
  quiz,
  isOwner,
  tutorUsername,
  firstName,
  loggedIn,
  squadAlias,
  dmReplyId = null,
}: QuizCardProps) {
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>('inline');
  const { slam, fire: fireSlam, clear: clearSlam } = useQuizSlam(quiz.totalQuestions);
  const hold = useHeldWork((state) => state.hold);
  useEffect(() => {
    if (phase === 'inline' || phase === 'result') return;
    return hold();
  }, [phase, hold]);
  const [attempt, setAttempt] = useState<QuizAttemptPublicDto | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [result, setResult] = useState<QuizAttemptResultDto | null>(null);
  const [flash, setFlash] = useState<{ optionId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answersRef = useRef<QuizAnswerInput[]>([]);
  const questionShownAtRef = useRef<number>(Date.now());
  const startedAtRef = useRef<number>(Date.now());
  const busyRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  const questions = attempt ? attempt.questions : quiz.questions;
  const total = quiz.totalQuestions;

  /**
   * Every step of the quiz funnel carries the same identity, so shown → started
   * → completed → gate can be read per quiz and per provider without a join.
   * The lead itself is not tracked here: it is the `auth_completed` the gate's
   * InlineAuth fires, so one event means "signed up" everywhere on the page.
   */
  const track = useCallback(
    (event: string, props?: Record<string, unknown>) => {
      posthog.capture(event, {
        quiz_id: quiz.id,
        provider_username: tutorUsername,
        creator_username: tutorUsername,
        tutor_username: tutorUsername,
        logged_in: loggedIn,
        is_owner: isOwner,
        total_questions: total,
        ...props,
      });
    },
    [quiz.id, isOwner, tutorUsername, loggedIn, total]
  );

  // The impression every rate is measured against: the card was on the page.
  // Once per mount, before any interaction.
  useEffect(() => {
    track('quiz_card_shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishQuiz = useCallback(
    async (attemptId: string, answers: QuizAnswerInput[]) => {
      if (cardRef.current) setLockedHeight(cardRef.current.offsetHeight);
      setPhase('finishing');
      const slamShownAt = Date.now();
      const elapsedMs = slamShownAt - startedAtRef.current;
      const completed = await completeQuizAttempt(attemptId, { answers, elapsedMs });
      if (!completed.success) {
        setError('Could not save your result — try again');
        setPhase('playing');
        return;
      }
      // A result that beats the animation is not a reason for the slam to flash.
      const held = Date.now() - slamShownAt;
      if (held < SLAM_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, SLAM_MS - held));
      }
      setResult(completed.data);
      track('quiz_completed', {
        score: completed.data.score,
        wrong_count: completed.data.totalQuestions - completed.data.score,
        top_percentile: completed.data.topPercentile,
        elapsed_ms: elapsedMs,
      });
      if (loggedIn) {
        setPhase('result');
        return;
      }
      recordQuizAttemptCompleted(quiz.id, attemptId);
      // The ask, separated from the answer to it: a completion that never
      // reaches this is a different loss from one that refuses the gate.
      track('quiz_gate_shown');
      setPhase('gate');
    },
    [loggedIn, quiz.id, track]
  );

  const openScore = useCallback(async () => {
    if (attempt) {
      await claimQuizAttempt(attempt.id);
      clearPendingQuizClaim(quiz.id);
    }
    setPhase('result');
  }, [attempt, quiz.id]);

  const onGateSuccess = useCallback(async () => {
    await openScore();
    router.refresh();
  }, [openScore, router]);

  // A page can carry several quizzes, and the gate is per card: signing in on
  // one re-renders the tree with `loggedIn` true, and the others must open
  // their own score rather than keep asking. Only the card that was signed in
  // on refreshes — one sign-in is one refresh, however many cards react to it.
  useEffect(() => {
    if (phase === 'gate' && loggedIn) void openScore();
  }, [phase, loggedIn, openScore]);

  const handleAnswer = useCallback(
    async (optionId: string) => {
      if (busyRef.current || flash) return;
      busyRef.current = true;
      setError(null);
      try {
        const tappedAt = Date.now();
        playAnswerBlip(answersRef.current.length);
        setFlash({ optionId });
        setPhase('playing');

        let liveAttempt = attempt;
        if (!liveAttempt) {
          const started = await startQuizAttempt(quiz.id, dmReplyId);
          if (!started.success) {
            setFlash(null);
            setError('Could not start the quiz — try again');
            return;
          }
          liveAttempt = started.data;
          startedAtRef.current = tappedAt;
          // The first answer is the start: an attempt is a question answered.
          track('quiz_started');
          if (!loggedIn) recordQuizAttemptStarted(quiz.id, liveAttempt.id);
        }

        const dealt = liveAttempt.questions;
        const answer: QuizAnswerInput = {
          questionId: dealt[currentIdx]!.id,
          optionId,
          timeMs: tappedAt - questionShownAtRef.current,
        };
        answersRef.current = [...answersRef.current, answer];

        const isLast = answersRef.current.length >= total;
        const attemptId = liveAttempt.id;
        const answers = answersRef.current;

        window.setTimeout(
          () => {
            setFlash(null);
            // Swapped in as the card moves on, never under the finger: the teaser
            // question came from the page payload and the attempt deals its own
            // order, so adopting it a moment early would reshuffle the options
            // being answered.
            setAttempt(liveAttempt);
            if (isLast) {
              playQuizComplete();
              void finishQuiz(attemptId, answers);
              return;
            }
            setCurrentIdx((i) => i + 1);
            questionShownAtRef.current = Date.now();

            fireSlam(currentIdx);
            // Every answer, not every third: the exact question an abandoned
            // attempt died on is analytics the throttle used to blur away.
            void updateQuizAttempt(attemptId, {
              answers,
              currentIndex: answers.length,
            });
          },
          Math.max(0, FLASH_MS - (Date.now() - tappedAt))
        );
      } finally {
        busyRef.current = false;
      }
    },
    [attempt, currentIdx, flash, total, fireSlam, quiz.id, loggedIn, track, finishQuiz, dmReplyId]
  );

  if (phase === 'result' && result) {
    // The score, then the questions worth a call — never their answers, which are
    // the call's. The card sells nothing: its calendar came out 2026-09-24, 7 buyers
    // a quarter beside the intro card's 17 for the same trial one column over.
    const missed = result.answers
      .filter((a) => !a.isCorrect)
      .map((a) => questions.find((q) => q.id === a.questionId)?.text)
      .filter((t): t is string => !!t);

    return (
      <QuizFinishedCard>
        <QuizResult
          score={result.score}
          total={result.totalQuestions}
          name={firstName}
          missed={missed}
          hardest={result.hardest}
        />
      </QuizFinishedCard>
    );
  }

  if (phase === 'gate') {
    return (
      <div
        style={lockedHeight ? { minHeight: lockedHeight } : undefined}
        className={`sf-quiz-card flex ${QUIZ_CARD_MIN_H} flex-col gap-3 rounded-[var(--sf-radius)] p-5 text-white`}
      >
        {/* No quiz title: this screen is one sentence and a door. */}
        <span className="sf-title text-title-2 font-extrabold">
          Your score is ready — sign in to see it
        </span>
        <div className="mt-auto">
          <InlineAuth
            id={`quiz-${quiz.id}`}
            source="quiz_answers"
            tutorUsername={tutorUsername}
            sourceSquad={squadAlias}
            tone="accent"
            cta="Show my score"
            onSuccess={onGateSuccess}
          />
        </div>
      </div>
    );
  }

  if (phase === 'finishing') {
    return (
      <div
        style={{
          ...(lockedHeight ? { minHeight: lockedHeight } : {}),
          ...slamDuration,
        }}
        className={`sf-quiz-card flex ${QUIZ_CARD_MIN_H} flex-col items-center justify-center rounded-[var(--sf-radius)] p-5 text-white`}
      >
        {/* The last slam of the run — a spinner and a 14px caption made the end
            of a game look like a form submitting. */}
        <Sealed className="sf-slam-word sf-slam-stay">THAT&rsquo;S A WRAP</Sealed>
      </div>
    );
  }

  const question = questions[currentIdx];
  if (!question) return null;

  return (
    <div
      ref={cardRef}
      className={`sf-quiz-card relative flex ${QUIZ_CARD_MIN_H} flex-col gap-3 overflow-hidden rounded-[var(--sf-radius)] p-5 text-white`}
    >
      <QuizSlam slam={slam} onDone={clearSlam}>
        <Sealed className="sf-slam-word">{slam?.text}</Sealed>
      </QuizSlam>
      <QuizProgressRow at={Math.min(currentIdx + 1, total)} of={total} inert={!!slam} />
      <div className="grid flex-1">
        {questions.map((q, qi) => {
          const active = qi === currentIdx;
          return (
            <div
              key={q.id}
              aria-hidden={!active}
              className={tw(
                'col-start-1 row-start-1 flex flex-col gap-2.5',
                !active && 'invisible',
                active && currentIdx > 0 && 'sf-q-switch'
              )}
            >
              <Sealed className="sf-title text-title-2 font-extrabold">{q.text}</Sealed>
              <div className="mt-auto flex flex-col gap-1.5">
                {q.options.map((option, i) => {
                  const isFlashed = active && flash?.optionId === option.id;
                  return (
                    <QuizOption
                      key={option.id}
                      index={i}
                      disabled={!active || !!flash}
                      flashed={isFlashed}
                      onPick={() => void handleAnswer(option.id)}
                    >
                      <Sealed>{option.label}</Sealed>
                    </QuizOption>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {error && <span className="text-p-3 font-semibold text-white">{error}</span>}
    </div>
  );
}
